import { supabase } from '@/integrations/supabase/client';
import { adminCacheService } from './AdminCacheService';
import { localStorageCacheService } from './LocalStorageCacheService';
import { debugLogger } from '@/utils/debugLogger';

interface StorageFile {
  name: string;
  id: string;
  updated_at: string;
  created_at: string;
  last_accessed_at: string;
  metadata: any;
  fullPath?: string;
  directory?: string;
}

interface PaginatedStorageResult {
  files: StorageFile[];
  hasMore: boolean;
  total: number;
}

export class OptimizedStorageService {
  private static instance: OptimizedStorageService;
  private readonly PAGE_SIZE = 50;
  private loadingStates = new Map<string, boolean>();
  private requestQueues = new Map<string, Promise<any>>();

  static getInstance(): OptimizedStorageService {
    if (!OptimizedStorageService.instance) {
      OptimizedStorageService.instance = new OptimizedStorageService();
    }
    return OptimizedStorageService.instance;
  }

  // Método otimizado para uma única chamada por bucket
  async getSingleBucketList(bucketName: string, forceRefresh: boolean = false): Promise<StorageFile[]> {
    const cacheKey = `single_${bucketName}_files`;
    
    // Verificar se já há uma requisição em andamento
    if (this.requestQueues.has(cacheKey)) {
      debugLogger.debug(`[OptimizedStorage] Request already in progress for ${bucketName}, waiting...`);
      return await this.requestQueues.get(cacheKey)!;
    }

    // Verificar cache
    if (!forceRefresh) {
      const cached = localStorageCacheService.get(cacheKey);
      if (cached && Array.isArray(cached)) {
        debugLogger.debug(`[OptimizedStorage] Returning cached data for ${bucketName}`);
        return cached as StorageFile[];
      }
    }

    // Criar promise para evitar requisições duplicadas
    const requestPromise = this.executeSingleBucketList(bucketName, cacheKey);
    this.requestQueues.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      this.requestQueues.delete(cacheKey);
    }
  }

  private async executeSingleBucketList(bucketName: string, cacheKey: string): Promise<StorageFile[]> {
    try {
      debugLogger.debug(`[OptimizedStorage] Making single API call for bucket: ${bucketName}`);
      
      // UMA única chamada para o bucket
      const { data, error } = await supabase.storage
        .from(bucketName)
        .list('', {
          limit: 1000,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) {
        debugLogger.error(`[OptimizedStorage] Error listing ${bucketName}:`, error);
        return [];
      }

      const files = (data || []).map(file => ({
        ...file,
        fullPath: file.name,
        directory: ''
      }));

      // Cachear resultado
      localStorageCacheService.set(cacheKey, files);
      adminCacheService.setStorageFiles(cacheKey, files, 10 * 60 * 1000); // 10 minutos

      debugLogger.debug(`[OptimizedStorage] Successfully fetched ${files.length} files from ${bucketName}`);
      return files;

    } catch (error) {
      debugLogger.error(`[OptimizedStorage] Error in executeSingleBucketList for ${bucketName}:`, error);
      return [];
    }
  }

  // Método para buscar múltiplos buckets com uma chamada cada
  async getMultipleBucketsOptimized(buckets: string[], forceRefresh: boolean = false): Promise<Record<string, StorageFile[]>> {
    const results: Record<string, StorageFile[]> = {};
    
    debugLogger.debug(`[OptimizedStorage] Fetching ${buckets.length} buckets with single call each`);
    
    // Fazer todas as chamadas em paralelo, mas uma por bucket
    const promises = buckets.map(async (bucket) => {
      try {
        const files = await this.getSingleBucketList(bucket, forceRefresh);
        return { bucket, files, error: null };
      } catch (error) {
        debugLogger.error(`[OptimizedStorage] Error loading bucket ${bucket}:`, error);
        return { bucket, files: [], error };
      }
    });

    const settled = await Promise.allSettled(promises);
    
    settled.forEach((result) => {
      if (result.status === 'fulfilled') {
        results[result.value.bucket] = result.value.files;
      }
    });

    debugLogger.debug(`[OptimizedStorage] Completed fetching all buckets:`, {
      bucketsRequested: buckets.length,
      bucketsCompleted: Object.keys(results).length,
      totalFiles: Object.values(results).reduce((sum, files) => sum + files.length, 0)
    });

    return results;
  }

  // Manter métodos existentes para compatibilidade
  async getAllBucketFilesRecursive(bucket: string, forceRefresh: boolean = false): Promise<StorageFile[]> {
    return this.getSingleBucketList(bucket, forceRefresh);
  }

  async getAllBucketsComplete(buckets: string[], forceRefresh: boolean = false): Promise<Record<string, StorageFile[]>> {
    return this.getMultipleBucketsOptimized(buckets, forceRefresh);
  }

  async getStorageFiles(bucket: string, page: number = 0, forceRefresh: boolean = false): Promise<PaginatedStorageResult> {
    const cacheKey = `${bucket}_page_${page}`;
    
    // Check cache first
    if (!forceRefresh) {
      const cached = adminCacheService.getStorageFiles(cacheKey);
      if (cached) {
        debugLogger.debug(`[OptimizedStorage] Returning cached data for ${bucket} page ${page}`);
        return cached;
      }
    }

    // Prevent multiple simultaneous requests
    if (this.loadingStates.get(cacheKey)) {
      await this.waitForLoad(cacheKey);
      const cachedAfterWait = adminCacheService.getStorageFiles(cacheKey);
      return cachedAfterWait || { files: [], hasMore: false, total: 0 };
    }

    this.loadingStates.set(cacheKey, true);

    try {
      const offset = page * this.PAGE_SIZE;
      
      const { data, error } = await supabase.storage
        .from(bucket)
        .list('', {
          limit: this.PAGE_SIZE,
          offset,
          sortBy: { column: 'updated_at', order: 'desc' }
        });

      if (error) {
        debugLogger.error(`[OptimizedStorage] Error loading ${bucket}:`, error);
        throw error;
      }

      const result: PaginatedStorageResult = {
        files: data || [],
        hasMore: (data?.length || 0) === this.PAGE_SIZE,
        total: (data?.length || 0) + offset
      };

      // Cache the result
      adminCacheService.setStorageFiles(cacheKey, result);
      
      return result;
    } finally {
      this.loadingStates.set(cacheKey, false);
    }
  }

  async getAllBucketFiles(bucket: string, forceRefresh: boolean = false): Promise<StorageFile[]> {
    // Redirect to recursive method for better coverage
    return this.getAllBucketFilesRecursive(bucket, forceRefresh);
  }

  async getAllBucketsData(buckets: string[], forceRefresh: boolean = false): Promise<Record<string, PaginatedStorageResult>> {
    const results: Record<string, PaginatedStorageResult> = {};
    
    // Use Promise.allSettled to handle bucket errors gracefully
    const promises = buckets.map(async (bucket) => {
      try {
        const data = await this.getStorageFiles(bucket, 0, forceRefresh);
        return { bucket, data, error: null };
      } catch (error) {
        debugLogger.error(`[OptimizedStorage] Error loading bucket ${bucket}:`, error);
        return { bucket, data: { files: [], hasMore: false, total: 0 }, error };
      }
    });

    const settled = await Promise.allSettled(promises);
    
    settled.forEach((result) => {
      if (result.status === 'fulfilled') {
        results[result.value.bucket] = result.value.data;
      }
    });

    return results;
  }

  async searchFiles(bucket: string, searchTerm: string): Promise<StorageFile[]> {
    const cacheKey = `search_${bucket}_${searchTerm}`;
    const cached = adminCacheService.getStorageFiles(cacheKey);
    
    if (cached && Array.isArray(cached)) {
      return cached as StorageFile[];
    }

    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .list('', {
          search: searchTerm,
          limit: 100,
          sortBy: { column: 'updated_at', order: 'desc' }
        });

      if (error) throw error;

      const files = data || [];
      adminCacheService.setStorageFiles(cacheKey, files);
      
      return files;
    } catch (error) {
      debugLogger.error(`[OptimizedStorage] Search error in ${bucket}:`, error);
      return [];
    }
  }

  clearCache(bucket?: string): void {
    if (bucket) {
      adminCacheService.invalidate(bucket);
      localStorageCacheService.remove(`single_${bucket}_files`);
      localStorageCacheService.remove(`recursive_${bucket}_files`);
    } else {
      adminCacheService.invalidate('storage_');
      adminCacheService.invalidate('search_');
      adminCacheService.invalidate('all_');
      adminCacheService.invalidate('recursive_');
      adminCacheService.invalidate('single_');
      localStorageCacheService.clear();
    }
  }

  searchInCache(bucket: string, searchTerm: string): StorageFile[] {
    const singleBucketKey = `single_${bucket}_files`;
    const recursiveKey = `recursive_${bucket}_files`;
    
    // Tentar cache otimizado primeiro
    let cached = localStorageCacheService.get(singleBucketKey);
    if (!cached) {
      // Fallback para cache recursivo
      cached = localStorageCacheService.get(recursiveKey);
    }
    if (!cached) {
      // Fallback para cache em memória
      cached = adminCacheService.getStorageFiles(singleBucketKey) || 
               adminCacheService.getStorageFiles(recursiveKey);
    }
    
    if (!cached || !Array.isArray(cached)) {
      return [];
    }

    const files = cached as StorageFile[];
    
    if (!searchTerm.trim()) {
      return files;
    }

    return files.filter(file => 
      file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (file.fullPath && file.fullPath.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }

  private async waitForLoad(cacheKey: string): Promise<void> {
    return new Promise((resolve) => {
      const check = () => {
        if (!this.loadingStates.get(cacheKey)) {
          resolve();
        } else {
          setTimeout(check, 100);
        }
      };
      check();
    });
  }

  getLoadingState(bucket: string, page: number = 0): boolean {
    return this.loadingStates.get(`${bucket}_page_${page}`) || false;
  }

  getAllFilesLoadingState(bucket: string): boolean {
    return this.loadingStates.get(`recursive_${bucket}_files`) || false;
  }

  getCacheStats() {
    return {
      memory: adminCacheService.getCacheStats(),
      localStorage: localStorageCacheService.getStats(),
      requestQueues: this.requestQueues.size
    };
  }
}

export const optimizedStorageService = OptimizedStorageService.getInstance();
